import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

/**
 * A dashboard entry "door" (feature 023, US1 — front.html `.bucket`).
 *
 * Three visual variants from the reference design:
 *  - `felt`  — the dark felt-green card (the Precision inventory)
 *  - `plain` — cream card on paper (default)
 *  - `brass` — pale card with a brass rule (the two shelf cards)
 */
export type DoorVariant = 'felt' | 'plain' | 'brass';

export interface DoorCardProps {
  /** Omit to render a static, non-interactive card (a room whose door isn't hung yet). */
  to?: string;
  /**
   * Opens the room by doing work rather than following a URL — the card becomes a
   * button. Used where the destination needs a session started first. Ignored when
   * `to` is given.
   */
  onClick?: () => void;
  /** Disables the button form while its click is in flight. */
  busy?: boolean;
  /**
   * Decorative mark shown above the title; hidden from assistive tech. An emoji string, or a
   * drawn icon (feature 033's Preempts card) that renders in a 64px slot.
   */
  icon: ReactNode;
  /** Wraps the icon in a tinted medallion — for cards that carry no CTA of their own. */
  iconMedallion?: boolean;
  title: string;
  description: string;
  /** The "→" call to action at the foot of the card. Omitted on static cards. */
  cta?: string;
  variant?: DoorVariant;
  /** Optional count pill (top-right). Hidden entirely when 0 or undefined. */
  count?: number;
  /** Optional difficulty chips row (Beginner / Intermediate / Expert). */
  levels?: boolean;
  /** Message shown under the call to action — e.g. why the room could not be opened. */
  notice?: ReactNode;
}

const CARD_BASE =
  'relative flex flex-col min-h-[11rem] rounded-2xl p-7 border shadow-md no-underline';

// Only a card you can actually walk through lifts.
const CARD_LIFT = 'transition-transform duration-100 hover:-translate-y-0.5';

const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2';

const VARIANT_CARD: Record<DoorVariant, string> = {
  felt: 'bg-felt border-felt-deep',
  plain: 'bg-card border-line',
  brass: 'bg-[#fff8e6] border-brass',
};

const VARIANT_TITLE: Record<DoorVariant, string> = {
  felt: 'text-cream',
  plain: 'text-ink',
  brass: 'text-ink',
};

const VARIANT_BODY: Record<DoorVariant, string> = {
  felt: 'text-cream-soft',
  plain: 'text-ink-soft',
  brass: 'text-ink-soft',
};

// Brass is decorative-only at 2.3:1 — the CTA is text, so each variant picks the
// brass tint that passes on its own surface: `brass-on-felt` on the dark felt
// (4.9:1; plain `brass` there is 3.5:1 and axe rejected it) and `brass-text` on
// the light cards (5.2:1).
const VARIANT_CTA: Record<DoorVariant, string> = {
  felt: 'text-brass-on-felt',
  plain: 'text-primary-600',
  brass: 'text-brass-text',
};

// The medallion is a tinted disc behind the emoji — decorative only, so these are
// background/ring tints and carry no text.
const VARIANT_MEDALLION: Record<DoorVariant, string> = {
  felt: 'bg-felt-deep ring-1 ring-[#2a6b4a]',
  plain: 'bg-[#e4eee7] ring-1 ring-[#c3ddcb]',
  brass: 'bg-[#f7ecd0] ring-1 ring-[#e3d3a5]',
};

export function DoorCard({
  to,
  onClick,
  busy = false,
  icon,
  iconMedallion = false,
  title,
  description,
  cta,
  variant = 'plain',
  count,
  levels = false,
  notice,
}: DoorCardProps) {
  const body = (
    <>
      {count !== undefined && count > 0 && (
        <span className="absolute top-4 right-4 rounded-full bg-[#8c1d1d] px-3 py-1 text-xs font-extrabold text-white">
          {count} waiting
        </span>
      )}

      {iconMedallion ? (
        <span
          className={`flex h-14 w-14 items-center justify-center rounded-full text-3xl ${VARIANT_MEDALLION[variant]}`}
          aria-hidden="true"
        >
          {icon}
        </span>
      ) : typeof icon === 'string' ? (
        <span className="text-3xl" aria-hidden="true">
          {icon}
        </span>
      ) : (
        <span className="inline-flex h-16 w-16 items-center justify-center" aria-hidden="true">
          {icon}
        </span>
      )}

      {/* h2: the cards sit directly under the page h1. The reference design marks these
          up as h3, but that would skip a heading level (axe heading-order); the visual
          size is carried by the classes, not the tag. */}
      <h2 className={`mt-3 mb-1.5 font-serif text-xl ${VARIANT_TITLE[variant]}`}>{title}</h2>

      <p className={`flex-1 text-sm leading-relaxed ${VARIANT_BODY[variant]}`}>{description}</p>

      {levels && (
        <span className="mt-3 flex gap-2">
          <span className="rounded-full bg-[#dff0e2] px-3 py-1 text-xs font-bold text-[#0b6b3a]">
            Beginner
          </span>
          <span className="rounded-full bg-[#fdeecb] px-3 py-1 text-xs font-bold text-[#8a6100]">
            Intermediate
          </span>
          <span className="rounded-full bg-[#fadada] px-3 py-1 text-xs font-bold text-[#8c1d1d]">
            Expert
          </span>
        </span>
      )}

    </>
  );

  const ctaClasses = `mt-3.5 text-sm font-bold ${VARIANT_CTA[variant]}`;

  if (to) {
    return (
      <Link
        to={to}
        className={`${CARD_BASE} ${CARD_LIFT} ${FOCUS_RING} ${VARIANT_CARD[variant]}`}
      >
        {body}
        {cta && <span className={ctaClasses}>{cta} &rarr;</span>}
      </Link>
    );
  }

  // The room needs a session started before it can be entered, so the card is driven by
  // a button rather than a link. The button is the CTA line, stretched over the whole
  // card by its ::after overlay — a heading may not sit inside a <button>, so the card
  // itself stays a plain container.
  if (onClick) {
    return (
      <div className={`${CARD_BASE} ${CARD_LIFT} ${VARIANT_CARD[variant]}`}>
        {body}
        <button
          type="button"
          onClick={onClick}
          disabled={busy}
          className={`${ctaClasses} ${FOCUS_RING} self-start text-left after:absolute after:inset-0 after:content-[''] disabled:cursor-not-allowed disabled:opacity-70`}
        >
          {cta} &rarr;
        </button>
        {/* Sits above the button's stretched overlay so its own controls stay clickable. */}
        {notice && <div className="relative mt-3 text-sm">{notice}</div>}
      </div>
    );
  }

  // No destination at all — render the same card as a plain container so it reads as a
  // room on the map rather than a link that goes nowhere.
  return (
    <div className={`${CARD_BASE} ${VARIANT_CARD[variant]}`}>
      {body}
      {cta && <span className={ctaClasses}>{cta} &rarr;</span>}
    </div>
  );
}
